import React, { useEffect, useState } from "react";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Plus, FolderKanban, Download, Link2, Pencil, Trash2, HardDrive, RefreshCw } from "lucide-react";

const EMPTY_CLIENT = { name: "", industry: "", target_audience: "", region: "", goals: "", tone_profile: "", drive_folder_id: "" };
const EMPTY_PROJECT = { name: "", campaign: "", mandatory_link: "", default_mode: "simulation" };

export default function Mandanten() {
  const { clients, current, select, refresh } = useClients();
  const { can } = useAuth();
  const [projects, setProjects] = useState([]);

  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [editingClientId, setEditingClientId] = useState(null);
  const [cOpen, setCOpen] = useState(false);

  const [projForm, setProjForm] = useState(EMPTY_PROJECT);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [pOpen, setPOpen] = useState(false);

  const [delClient, setDelClient] = useState(null);
  const [delProject, setDelProject] = useState(null);

  const [driveConnected, setDriveConnected] = useState(false);
  const [driveEmail, setDriveEmail] = useState(null);
  const [driveFolder, setDriveFolder] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);
  const [syncInterval, setSyncInterval] = useState(60);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [syncState, setSyncState] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);

  const loadProjects = () => { if (current) api.get(`/projects?client_id=${current.id}`).then(({ data }) => setProjects(data)); };
  useEffect(() => { loadProjects(); }, [current]);
  useEffect(() => { setDriveFolder(current?.drive_folder_id || ""); }, [current]);
  useEffect(() => {
    if (!current) return;
    api.get(`/oauth/drive/status?client_id=${current.id}`).then(({ data }) => { setDriveConnected(data.connected); setDriveEmail(data.google_email); }).catch(() => {});
    loadSync();
  }, [current]);

  const loadSync = () => {
    if (!current) return;
    api.get(`/drive/sync-status?client_id=${current.id}`).then(({ data }) => setSyncState((data.states || [])[0] || null)).catch(() => {});
    setSyncInterval(current?.drive_sync_interval_minutes || 60);
    setSyncEnabled(current?.drive_sync_enabled !== false);
  };
  const saveSyncConfig = async () => {
    try {
      await api.put("/drive/sync-config", { client_id: current.id, enabled: syncEnabled, interval_minutes: Number(syncInterval) });
      toast.success("Auto-Sync-Einstellungen gespeichert"); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const syncNow = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/drive/sync-now", { client_id: current.id, full: false });
      if (data.status === "already_running") toast.info("Ein Sync für diesen Mandanten läuft bereits");
      else if (data.status === "error") toast.error(`Sync-Fehler: ${data.message}`);
      else toast.success(`Sync: ${data.added || 0} neu · ${data.updated || 0} aktualisiert · ${data.missing || 0} fehlend`);
      loadSync(); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSyncBusy(false); }
  };
  useEffect(() => {
    if (window.location.search.includes("drive_connected=true")) {
      toast.success("Google Drive verbunden");
      window.history.replaceState({}, "", "/mandanten");
    }
  }, []);

  const connectDrive = async () => {
    try { const { data } = await api.get(`/oauth/drive/connect?client_id=${current.id}`); window.location.href = data.authorization_url; }
    catch (e) { toast.error(apiError(e)); }
  };
  const loadDrive = async () => {
    if (!driveFolder.trim()) return toast.error("Bitte Drive-Ordner-ID oder Link eingeben");
    setDriveBusy(true);
    try {
      const { data } = await api.post("/drive/import", { client_id: current.id, folder: driveFolder });
      toast.success(`Drive-Import: ${data.added} neu, ${data.updated} aktualisiert (${data.total} Dateien)`);
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setDriveBusy(false); }
  };

  // ---- client create/edit ----
  const openNewClient = () => { setEditingClientId(null); setClientForm(EMPTY_CLIENT); setCOpen(true); };
  const openEditClient = (c) => {
    setEditingClientId(c.id);
    setClientForm({ name: c.name || "", industry: c.industry || "", target_audience: c.target_audience || "", region: c.region || "", goals: c.goals || "", tone_profile: c.tone_profile || "", drive_folder_id: c.drive_folder_id || "" });
    setCOpen(true);
  };
  const saveClient = async () => {
    if (!clientForm.name) return toast.error("Name erforderlich");
    try {
      if (editingClientId) {
        const { data } = await api.patch(`/clients/${editingClientId}`, clientForm);
        toast.success("Mandant aktualisiert"); await refresh(); select(data);
      } else {
        const { data } = await api.post("/clients", clientForm);
        toast.success("Mandant angelegt"); await refresh(); select(data);
      }
      setCOpen(false); setClientForm(EMPTY_CLIENT); setEditingClientId(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteClient = async () => {
    try { await api.delete(`/clients/${delClient.id}`); toast.success("Mandant gelöscht"); setDelClient(null); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- project create/edit ----
  const openNewProject = () => { setEditingProjectId(null); setProjForm(EMPTY_PROJECT); setPOpen(true); };
  const openEditProject = (p) => {
    setEditingProjectId(p.id);
    setProjForm({ name: p.name || "", campaign: p.campaign || "", mandatory_link: p.mandatory_link || "", default_mode: p.default_mode || "simulation" });
    setPOpen(true);
  };
  const saveProject = async () => {
    if (!projForm.name) return toast.error("Name erforderlich");
    try {
      if (editingProjectId) {
        await api.patch(`/projects/${editingProjectId}`, { ...projForm, client_id: current.id });
        toast.success("Projekt aktualisiert");
      } else {
        await api.post("/projects", { ...projForm, client_id: current.id });
        toast.success("Projekt angelegt");
      }
      setPOpen(false); setProjForm(EMPTY_PROJECT); setEditingProjectId(null); loadProjects(); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteProject = async () => {
    try { const { data } = await api.delete(`/projects/${delProject.id}`); toast.success(`Projekt gelöscht (${data.posts_deleted} Posts entfernt)`); setDelProject(null); loadProjects(); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const exportClient = async () => {
    const { data } = await api.get(`/clients/${current.id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `${current.name}_export.json`; a.click();
    toast.success("DSGVO-Export erstellt");
  };

  return (
    <div>
      <PageHeader overline="Struktur" title="Mandanten & Projekte" subtitle="Interne Organisationseinheiten – Kunden haben keinen Login. Anlegen, umbenennen und löschen."
        actions={can("admin") && (
          <Button data-testid="client-add-btn" onClick={openNewClient} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Plus size={15} className="mr-1.5" /> Mandant anlegen</Button>
        )} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client list */}
        <div className="space-y-2">
          <div className="overline mb-1">Mandanten ({clients.length})</div>
          {clients.map((c) => (
            <div key={c.id} data-testid={`mandant-item-${c.id}`}
              className={`group w-full p-4 rounded-md border transition-colors duration-150 ${current?.id === c.id ? "border-[#0A0A0A] bg-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
              <div className="flex items-start gap-2">
                <button onClick={() => select(c)} className="flex-1 text-left">
                  <div className="flex items-center gap-2"><Building2 size={16} className="text-slate-500" /><span className="font-semibold text-sm">{c.name}</span></div>
                  <div className="text-xs text-slate-400 mt-1">{c.industry || "—"} · {c.region || "—"}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-mono">{c.project_count} Projekte · {c.asset_count} Assets</div>
                </button>
                {can("admin") && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`client-edit-${c.id}`} onClick={() => openEditClient(c)} className="p-1.5 rounded-sm hover:bg-slate-100"><Pencil size={14} className="text-slate-500" /></button>
                    <button data-testid={`client-delete-${c.id}`} onClick={() => setDelClient(c)} className="p-1.5 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-500" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Current client detail */}
        <div className="lg:col-span-2">
          {current && (
            <Card className="p-6 rounded-md border-slate-200 shadow-none">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold">{current.name}</h2>
                  <p className="text-sm text-slate-500 mt-1">{current.goals || "Keine Ziele definiert"}</p>
                </div>
                <div className="flex gap-2">
                  {can("admin") && <Button variant="outline" size="sm" data-testid="client-edit-current" onClick={() => openEditClient(current)} className="rounded-full"><Pencil size={14} className="mr-1.5" /> Bearbeiten</Button>}
                  {can("admin") && <Button variant="outline" size="sm" data-testid="client-export-btn" onClick={exportClient} className="rounded-full"><Download size={14} className="mr-1.5" /> Export</Button>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                <Info label="Branche" value={current.industry} />
                <Info label="Zielgruppe" value={current.target_audience} />
                <Info label="Region" value={current.region} />
                <Info label="Tonalität" value={current.tone_profile} />
              </div>

              {/* Google Drive */}
              <div className="mb-6 p-4 rounded-md border border-slate-200 bg-slate-50/60">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline flex items-center gap-1.5"><HardDrive size={13} /> Google Drive Assets</div>
                  <span className={`text-xs font-medium flex items-center gap-1 ${driveConnected ? "text-emerald-600" : "text-slate-400"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${driveConnected ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {driveConnected ? (driveEmail ? `Verbunden · ${driveEmail}` : "Verbunden") : "Nicht verbunden"}
                  </span>
                </div>
                {!driveConnected ? (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">Verbinde einmalig dein Google-Konto, um Ordner pro Mandant einzulesen.</p>
                    {can("admin") && <Button size="sm" data-testid="drive-connect-btn" onClick={connectDrive} className="rounded-full bg-[#0A0A0A] shrink-0"><HardDrive size={14} className="mr-1.5" /> Verbinden</Button>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input data-testid="drive-folder-input" placeholder="Drive-Ordner-ID oder Ordner-Link" value={driveFolder} onChange={(e) => setDriveFolder(e.target.value)} className="h-9 text-sm flex-1" />
                    {can("admin") && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="drive-reconnect-btn"
                        onClick={connectDrive}
                        className="rounded-full shrink-0"
                      >
                        <HardDrive size={14} className="mr-1.5" /> Neu verbinden
                      </Button>
                    )}
                    <Button size="sm" data-testid="drive-load-btn" onClick={loadDrive} disabled={driveBusy} className="rounded-full bg-[#0A0A0A] shrink-0">
                      {driveBusy ? <RefreshCw size={14} className="animate-spin" /> : <><RefreshCw size={14} className="mr-1.5" /> Assets laden</>}
                    </Button>
                  </div>
                )}
                {driveConnected && can("admin") && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid="drive-sync-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" data-testid="drive-sync-enabled" checked={syncEnabled} onChange={(e) => setSyncEnabled(e.target.checked)} className="accent-black" />
                        Auto-Sync alle
                        <Input type="number" min="1" data-testid="drive-sync-interval" value={syncInterval} onChange={(e) => setSyncInterval(e.target.value)} className="h-7 w-16 text-sm" />
                        Min.
                      </label>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" data-testid="drive-sync-save" onClick={saveSyncConfig} className="rounded-full h-8">Speichern</Button>
                        <Button size="sm" data-testid="drive-sync-now-btn" onClick={syncNow} disabled={syncBusy} className="rounded-full h-8 bg-[#0A0A0A]">
                          {syncBusy ? <RefreshCw size={13} className="animate-spin" /> : <><RefreshCw size={13} className="mr-1.5" /> Jetzt synchronisieren</>}
                        </Button>
                      </div>
                    </div>
                    {syncState && (
                      <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap" data-testid="drive-sync-state">
                        <span className={`h-1.5 w-1.5 rounded-full ${syncState.last_status === "error" ? "bg-rose-500" : syncState.last_status === "ok" ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <span>Status: {syncState.last_status || "—"}</span>
                        {syncState.last_sync_at && <span>· zuletzt {new Date(syncState.last_sync_at).toLocaleString("de-DE")}</span>}
                        {syncState.last_result && <span>· +{syncState.last_result.added || 0}/~{syncState.last_result.updated || 0}/-{syncState.last_result.missing || 0}</span>}
                        {syncState.last_error_redacted && <span className="text-rose-600">· {syncState.last_error_redacted}</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="overline">Projekte / Kampagnen</div>
                {can("admin") && <Button size="sm" variant="outline" data-testid="project-add-btn" onClick={openNewProject} className="rounded-full h-8"><Plus size={14} className="mr-1" /> Projekt</Button>}
              </div>
              <div className="space-y-2">
                {projects.length === 0 && <div className="text-sm text-slate-400 py-4">Noch keine Projekte.</div>}
                {projects.map((p) => (
                  <div key={p.id} data-testid={`project-item-${p.id}`} className="group flex items-center gap-3 p-3 border border-slate-200 rounded-md">
                    <FolderKanban size={16} className="text-slate-400" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{p.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-2">{p.campaign || "—"} {p.mandatory_link && <span className="flex items-center gap-1 text-slate-500"><Link2 size={11} />{p.mandatory_link}</span>}</div>
                    </div>
                    <div className="text-right text-xs text-slate-400 font-mono mr-2">{p.post_count} Posts · {p.account_count} Accounts</div>
                    {can("admin") && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button data-testid={`project-edit-${p.id}`} onClick={() => openEditProject(p)} className="p-1.5 rounded-sm hover:bg-slate-100"><Pencil size={14} className="text-slate-500" /></button>
                        <button data-testid={`project-delete-${p.id}`} onClick={() => setDelProject(p)} className="p-1.5 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-500" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Client dialog (create/edit) */}
      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editingClientId ? "Mandant bearbeiten" : "Neuer Mandant"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {[["name", "Name *"], ["industry", "Branche"], ["target_audience", "Zielgruppe"], ["region", "Region"], ["goals", "Kampagnenziele"], ["tone_profile", "Tonalität"], ["drive_folder_id", "Google Drive Ordner-ID"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`client-field-${k}`} className="mt-1" value={clientForm[k]} onChange={(e) => setClientForm({ ...clientForm, [k]: e.target.value })} /></div>
            ))}
          </div>
          <DialogFooter><Button data-testid="client-save-btn" onClick={saveClient} className="rounded-full bg-[#0A0A0A]">{editingClientId ? "Speichern" : "Anlegen"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project dialog (create/edit) */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editingProjectId ? "Projekt bearbeiten" : `Neues Projekt für ${current?.name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input data-testid="project-name" className="mt-1" value={projForm.name} onChange={(e) => setProjForm({ ...projForm, name: e.target.value })} /></div>
            <div><Label className="text-xs">Kampagne</Label><Input data-testid="project-campaign" className="mt-1" value={projForm.campaign} onChange={(e) => setProjForm({ ...projForm, campaign: e.target.value })} /></div>
            <div><Label className="text-xs">Pflichtlink (Landingpage)</Label><Input data-testid="project-link" className="mt-1" value={projForm.mandatory_link} onChange={(e) => setProjForm({ ...projForm, mandatory_link: e.target.value })} placeholder="https://…" /></div>
          </div>
          <DialogFooter><Button data-testid="project-save-btn" onClick={saveProject} className="rounded-full bg-[#0A0A0A]">{editingProjectId ? "Speichern" : "Anlegen"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete client confirm */}
      <AlertDialog open={!!delClient} onOpenChange={(o) => !o && setDelClient(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mandant „{delClient?.name}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>Alle Projekte, Assets, Posts und Audit-Einträge dieses Mandanten werden unwiderruflich gelöscht (DSGVO-Löschung).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="client-delete-confirm" onClick={deleteClient} className="bg-rose-600 hover:bg-rose-700">Endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete project confirm */}
      <AlertDialog open={!!delProject} onOpenChange={(o) => !o && setDelProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt „{delProject?.name}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>Das Projekt und alle zugehörigen Posts ({delProject?.post_count || 0}) & Accounts werden gelöscht. Assets bleiben in der Medienbibliothek erhalten.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="project-delete-confirm" onClick={deleteProject} className="bg-rose-600 hover:bg-rose-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Info = ({ label, value }) => (<div><div className="overline mb-0.5">{label}</div><div className="text-slate-700">{value || "—"}</div></div>);
